<script lang="ts" setup>
  import PDFViewer from '@/components/pdf/pdfViewer.vue'
  import { useUserStore } from '@/shared/stores/userStore'
  import { useCaseStore } from '@/store/app/case/detail/caseDetailStore'
  import { useGlobalChatStore } from '@/store/app/globalChat/globalChatStore'
  import {
    type AIChatSession,
    type AIChatSessionMessage,
    AiChatSessionsService,
    AIChatSessionTypeEnum,
    SessionStateEnum,
    type CustomerCase as Case,
    DocumentsService,
    type SessionGroup,
    type CustomerUser as User
  } from '@/types/generated'
  import { useChatPdfExport } from '@/composables/useChatPdfExport'
  import { useChannelSubscription } from '@/composables/useChannelSubscription'
  import { useFeatureFlag } from '@/composables/useFeatureFlag'
  import { useVerdictHintState } from '@/composables/useVerdictHintState'
  import { WebSocketMessageType } from '@/utils/channels/types'
  import { ROUTES } from '@/router/routes'
  import { computed, nextTick, onMounted, onUnmounted, provide, ref, watch } from 'vue'
  import { useRoute } from 'vue-router'
  import ChatFolderDialog from '../global/components/ChatFolderDialog.vue'
  import FolderView from '../global/components/FolderView.vue'
  import AiChatDebugPanel from './AiChatDebugPanel.vue'
  import AiChatMain from './AiChatMain.vue'
  import AiChatMainSkeleton from './AiChatMainSkeleton.vue'
  import AiChatSidebar from './AiChatSidebar.vue'
  import AiChatSidebarSkeleton from './AiChatSidebarSkeleton.vue'

  const emit = defineEmits<{
    folderSelected: [folder: SessionGroup | null]
    folderUpdated: [folder: SessionGroup]
  }>()

  // Props for different chat contexts
  interface Props {
    caseUuid?: string
    sessionGroupUuid?: string
    sessionGroup?: SessionGroup
    sessionType?: AIChatSessionTypeEnum
  }

  const props = withDefaults(defineProps<Props>(), {
    caseUuid: undefined,
    sessionGroupUuid: undefined,
    sessionGroup: undefined,
    sessionType: AIChatSessionTypeEnum.CASE
  })

  const caseStore = useCaseStore()
  const userStore = useUserStore()
  const globalChatStore = useGlobalChatStore()
  const caseRecord = computed<Case>(() => caseStore.caseRecord as Case)
  const currentUser = computed<User | null>(() => userStore.user as User | null)

  // Determine if we're in case or global context
  const isCaseContext = computed(
    () => props.sessionType === AIChatSessionTypeEnum.CASE || !!props.caseUuid
  )
  const isGlobalContext = computed(
    () => props.sessionType === AIChatSessionTypeEnum.GLOBAL || !!props.sessionGroupUuid
  )

  // Determine if we should show folder view (when a folder is selected but no specific chat session)
  const shouldShowFolderView = computed(() => {
    return isGlobalContext.value && selectedFolder.value && !activeChatSession.value
  })

  // Loading state - only show skeleton during the very first load to avoid remounts on refresh
  const userLoading = ref(false)
  const hasLoadedOnce = ref(false)
  // Mark as loaded once when global store finishes first load
  watch(
    () => (isGlobalContext.value ? globalChatStore.loading : caseStore.isLoadingCase),
    (isLoading) => {
      if (!isLoading) hasLoadedOnce.value = true
    },
    { immediate: true }
  )
  const isInitialLoading = computed(() => {
    // Show loading if user is loading
    if (userLoading.value) return true

    // For case context, only show skeleton if not loaded once yet
    if (isCaseContext.value) {
      return caseStore.isLoadingCase && !hasLoadedOnce.value
    }

    // For global context, only show skeleton if not loaded once yet
    if (isGlobalContext.value) {
      return globalChatStore.loading && !hasLoadedOnce.value
    }

    return false
  })

  // Selected folder state
  const selectedFolder = ref<SessionGroup | null>(props.sessionGroup || null)

  // Watch for session group updates in the store and update selectedFolder
  watch(
    () => globalChatStore.sessionGroups,
    (newSessionGroups) => {
      if (selectedFolder.value) {
        const updatedGroup = newSessionGroups.find(
          (group) => group.uuid === selectedFolder.value?.uuid
        )
        if (updatedGroup) {
          selectedFolder.value = updatedGroup
        } else {
          // Folder was deleted, clear the selection to avoid showing stale folder view
          selectedFolder.value = null
        }
      }
    },
    { deep: true }
  )

  // Dialog state
  const showFolderDialog = ref(false)
  const editingFolder = ref<SessionGroup | null>(null)
  const sessionToMoveToNewFolder = ref<AIChatSession | null>(null)

  // Refresh the court database feature flag every 5 minutes so changes in PostHog
  // are picked up without requiring a page reload.
  let flagRefreshInterval: ReturnType<typeof setInterval> | null = null
  const FLAG_REFRESH_MS = 5 * 60 * 1000

  // Fetch user if not already loaded
  onMounted(async () => {
    courtDatabaseFlag.checkFlag()
    flagRefreshInterval = setInterval(() => {
      courtDatabaseFlag.clearCache()
      courtDatabaseFlag.checkFlag()
    }, FLAG_REFRESH_MS)
    if (!userStore.user) {
      try {
        userLoading.value = true
        await userStore.fetchUser()
      } catch (error) {
        console.error('Failed to fetch user:', error)
      } finally {
        userLoading.value = false
      }
    }
  })

  // Disconnect WebSocket on navigation/unmount
  onUnmounted(() => {
    wsUnsubscribe()
    if (flagRefreshInterval !== null) {
      clearInterval(flagRefreshInterval)
    }
  })

  provide('currentUser', currentUser)
  provide('caseData', isCaseContext.value ? caseRecord : ref(null))
  provide('sessionType', props.sessionType)
  provide(
    'sessionGroupData',
    computed(() => selectedFolder.value)
  )

  const lastResponseTime = ref<number | null>(null)
  provide('lastResponseTime', lastResponseTime)

  // Use the global store's activeChatSession for reactivity
  const activeChatSession = computed(() => globalChatStore.activeChatSession)
  provide('activeChatSession', activeChatSession)

  // Verdict grounding feature flag — provided to all chat child components
  const courtDatabaseFlag = useFeatureFlag('COURT_DATABASE')
  provide('courtDatabaseFlagEnabled', courtDatabaseFlag.isEnabled)

  // Court database toggle state — injected by AiChatMessageInput and included in send_message
  const courtDbEnabled = ref(false)
  provide('courtDbEnabled', courtDbEnabled)

  const setCourtDbEnabled = async (value: boolean) => {
    courtDbEnabled.value = value
    if (activeChatSession.value) {
      await AiChatSessionsService.aiChatSessionsPartialUpdate({
        uuid: activeChatSession.value.uuid,
        requestBody: { court_db_enabled: value }
      })
    }
  }
  provide('setCourtDbEnabled', setCourtDbEnabled)

  const sessionUuid = computed(() => activeChatSession.value?.uuid)
  const { verdictHintShown, markVerdictHintShown } = useVerdictHintState(sessionUuid)
  provide('verdictHintShown', verdictHintShown)
  provide('markVerdictHintShown', markVerdictHintShown)

  const generatingSessionUuids = ref(new Set<string>())

  // WebSocket streaming state
  const streamingText = ref('')
  const streamingChunks = ref<any[]>([])
  const streamingVerdictContext = ref<any[]>([])
  const wsChannelName = ref('')
  const {
    subscribe: wsSubscribe,
    unsubscribe: wsUnsubscribe,
    sendMessage: wsSendMessage,
    lastMessage: wsLastMessage
  } = useChannelSubscription(wsChannelName, {
    disableAutoLifecycle: true,
    autoReconnect: false
  })

  // Watch for active session changes and fetch full session data
  watch(
    () => activeChatSession.value?.uuid,
    async (newUuid, oldUuid) => {
      if (newUuid && newUuid !== oldUuid) {
        // Don't fetch if this session is currently generating a response
        if (generatingSessionUuids.value.has(newUuid)) {
          console.log('Skipping full session fetch - session is generating response')
          return
        }

        const fullSession = await AiChatSessionsService.aiChatSessionsRetrieve({
          uuid: newUuid
        })

        // Check if the active session is still the same UUID we fetched for
        // This prevents race conditions when quickly switching between sessions
        if (activeChatSession.value?.uuid !== newUuid) {
          return
        }

        // Preserve any local messages that might not be on the server yet
        const currentSession = activeChatSession.value
        if (currentSession && currentSession.messages && fullSession.messages) {
          // If the server has fewer messages than we have locally, preserve local messages
          if (currentSession.messages.length > fullSession.messages.length) {
            console.log('Preserving local messages that are not yet on server')
            fullSession.messages = currentSession.messages
          }
        }

        // Restore court DB preference for this session
        courtDbEnabled.value = fullSession.court_db_enabled ?? false

        // Update active chat session with spread to ensure reactivity
        globalChatStore.setActiveChatSession({ ...fullSession })

        // Update in the chat_sessions array
        if (isCaseContext.value) {
          const sessionIndex = caseRecord.value.chat_sessions.findIndex((s) => s.uuid === newUuid)
          if (sessionIndex !== -1) {
            caseRecord.value.chat_sessions[sessionIndex] = { ...fullSession }
          }
        }
        // For global context, session updates will be handled by the parent component or global store
      }
    }
  )

  const changeActiveChatSession = (chatSession: AIChatSession) => {
    globalChatStore.setActiveChatSession({ ...chatSession })
  }
  provide('changeActiveChatSession', changeActiveChatSession)
  const createNewChatSession = async (sessionGroupUuid?: string) => {
    let requestBody: any = {}

    if (isCaseContext.value) {
      requestBody.case_uuid = props.caseUuid || caseRecord.value.uuid
      requestBody.session_type = AIChatSessionTypeEnum.CASE
    } else if (isGlobalContext.value) {
      // Explicitly set session_type for global chats
      requestBody.session_type = AIChatSessionTypeEnum.GLOBAL
      // Use provided sessionGroupUuid or fall back to props
      const groupUuid = sessionGroupUuid || props.sessionGroupUuid
      if (groupUuid) {
        requestBody.session_group_uuid = groupUuid
      }
      // Set a default title for all global chats so title update logic works correctly
      requestBody.title = 'New Chat'
    }

    const session = await AiChatSessionsService.aiChatSessionsCreate({
      requestBody
    })

    // Update the appropriate context's session list
    if (isCaseContext.value) {
      caseRecord.value.chat_sessions.unshift(session)
    } else if (isGlobalContext.value) {
      // Update the global chat store
      const globalChatStore = useGlobalChatStore()
      globalChatStore.chatSessions.unshift(session)
    }

    // Set as active session immediately so it can be selected right away
    globalChatStore.setActiveChatSession(session)

    return session
  }
  provide('createNewChatSession', createNewChatSession)

  const isSessionGenerating = (sessionUuid: string) => {
    return generatingSessionUuids.value.has(sessionUuid)
  }
  const isAnySessionGenerating = computed(() => generatingSessionUuids.value.size > 0)

  provide('generatingSessionUuids', generatingSessionUuids)
  provide('isSessionGenerating', isSessionGenerating)
  provide('isAnySessionGenerating', isAnySessionGenerating)
  provide('streamingText', streamingText)
  provide('streamingChunks', streamingChunks)
  provide('streamingVerdictContext', streamingVerdictContext)

  // Track the session being streamed so the WebSocket message watcher can access it
  const streamingSession = ref<AIChatSession | null>(null)
  let placeholderPushed = false

  const contentEvents = new Set([
    WebSocketMessageType.AI_CHAT_TEXT_DELTA,
    WebSocketMessageType.AI_CHAT_TEXT_CHUNK_END,
    WebSocketMessageType.AI_CHAT_CHUNK,
    WebSocketMessageType.AI_CHAT_MESSAGE_COMPLETE
  ])

  const updateSessionState = (session: AIChatSession, state: SessionStateEnum) => {
    if (activeChatSession.value?.uuid === session.uuid) {
      globalChatStore.setActiveChatSession({
        ...activeChatSession.value,
        session_state: state
      })
    }
    if (isCaseContext.value) {
      const idx = caseRecord.value.chat_sessions.findIndex((s) => s.uuid === session.uuid)
      if (idx !== -1) {
        caseRecord.value.chat_sessions[idx] = {
          ...caseRecord.value.chat_sessions[idx],
          session_state: state
        }
      }
    }
  }

  const cleanupStreaming = (session: AIChatSession) => {
    wsUnsubscribe()
    wsChannelName.value = ''
    streamingText.value = ''
    streamingChunks.value = []
    streamingVerdictContext.value = []
    generatingSessionUuids.value.delete(session.uuid)
    streamingSession.value = null
    placeholderPushed = false
    updateSessionState(session, SessionStateEnum.READY)
  }

  // Watch WebSocket messages and handle streaming events
  watch(wsLastMessage, (message) => {
    if (!message || !streamingSession.value) return
    const session = streamingSession.value

    // Push placeholder on first content event
    if (!placeholderPushed && contentEvents.has(message.type)) {
      const placeholderMessage = {
        content: { chunks: [], case_specific_info_refused: false },
        role: 'assistant',
        uuid: crypto.randomUUID(),
        created_at: new Date().toISOString(),
        _isStreaming: true
      } as unknown as AIChatSessionMessage
      session.messages.push(placeholderMessage)
      globalChatStore.setActiveChatSession({ ...session })
      placeholderPushed = true
    }

    switch (message.type) {
      case WebSocketMessageType.AI_CHAT_SESSION_STATE_CHANGE:
        updateSessionState(session, message.data.state)
        break

      case WebSocketMessageType.AI_CHAT_TEXT_DELTA:
        streamingText.value += message.data.delta
        break

      case WebSocketMessageType.AI_CHAT_TEXT_CHUNK_END:
        if (streamingText.value) {
          streamingChunks.value.push({ type: 'text', text: streamingText.value })
          streamingText.value = ''
        }
        break

      case WebSocketMessageType.AI_CHAT_CHUNK:
        streamingChunks.value.push(message.data)
        break

      case WebSocketMessageType.AI_CHAT_VERDICT_CONTEXT:
        streamingVerdictContext.value = message.data.verdicts ?? []
        break

      case WebSocketMessageType.AI_CHAT_MESSAGE_COMPLETE: {
        const completeMessage = message.data.message as AIChatSessionMessage
        const idx = session.messages.findIndex((m: any) => m._isStreaming === true)
        if (idx !== -1) {
          session.messages[idx] = completeMessage
        } else {
          session.messages[session.messages.length - 1] = completeMessage
        }
        globalChatStore.setActiveChatSession({ ...session })
        break
      }

      case WebSocketMessageType.AI_CHAT_DONE:
        debugPanelRef.value?.endResponseTimer()
        cleanupStreaming(session)
        break

      case WebSocketMessageType.AI_CHAT_ERROR:
        console.error('WebSocket stream error:', message.data.message)
        cleanupStreaming(session)
        break
    }
  })

  const sendChatMessage = async (session: AIChatSession | null, message: string) => {
    // clean up any previous streaming session before starting a new one
    if (streamingSession.value) {
      cleanupStreaming(streamingSession.value)
    }

    if (!activeChatSession.value) {
      session = await createNewChatSession()
      await nextTick()
      session = activeChatSession.value || session
    }

    generatingSessionUuids.value.add(session!.uuid)
    debugPanelRef.value?.startResponseTimer()

    // Update title if it's still "New Chat" or empty/null and this is the first user message
    const messages = session!.messages || []
    const userMessageCount = Array.isArray(messages)
      ? messages.filter((msg) => msg.role === 'user').length
      : 0
    const needsTitleUpdate =
      (!session!.title || session!.title === 'New Chat' || session!.title.trim() === '') &&
      userMessageCount === 0
    if (needsTitleUpdate) {
      const newTitle = message.length > 50 ? message.substring(0, 50) + '...' : message
      try {
        await AiChatSessionsService.aiChatSessionsPartialUpdate({
          uuid: session!.uuid,
          requestBody: {
            title: newTitle
          }
        })
        session!.title = newTitle
        globalChatStore.updateChatSession(session!)
      } catch (titleUpdateError) {
        console.error('Failed to update chat session title:', titleUpdateError)
      }
    }

    session!.messages.push({
      content: message,
      role: 'user',
      user_uuid: currentUser.value?.uuid || ''
    } as AIChatSessionMessage)

    globalChatStore.setActiveChatSession(session!)

    streamingText.value = ''
    streamingChunks.value = []
    streamingVerdictContext.value = []
    placeholderPushed = false
    streamingSession.value = session!

    // Connect WebSocket and send message
    wsChannelName.value = `ai_chat_session_${session!.uuid}`
    await wsSubscribe()
    wsSendMessage({
      type: 'send_message',
      data: { content: message, court_database_flag_enabled: courtDatabaseFlag.isEnabled.value }
    })
  }
  provide('sendChatMessage', sendChatMessage)

  const reaskWithCourtDb = async () => {
    const session = activeChatSession.value
    if (!session) return
    if (streamingSession.value) {
      cleanupStreaming(streamingSession.value)
    }
    await setCourtDbEnabled(true)
    generatingSessionUuids.value.add(session.uuid)
    debugPanelRef.value?.startResponseTimer()
    streamingText.value = ''
    streamingChunks.value = []
    streamingVerdictContext.value = []
    placeholderPushed = false
    streamingSession.value = session
    wsChannelName.value = `ai_chat_session_${session.uuid}`
    await wsSubscribe()
    wsSendMessage({ type: 'reask_with_court_db', data: {} })
  }
  provide('reaskWithCourtDb', reaskWithCourtDb)

  const isDocumentPanelOpen = ref(false)
  provide('isDocumentPanelOpen', isDocumentPanelOpen)

  // PDF viewer state
  const isPdfOpen = ref(false)
  const pdfUrl = ref('')
  const pdfFilename = ref('')
  const pdfDocument = ref<{ filesize?: number; page_count?: number } | undefined>(undefined)
  const pdfInitialPage = ref(1)
  provide('isPdfOpen', isPdfOpen)

  const handleDocumentClick = async (document: any) => {
    try {
      const response = await DocumentsService.documentsRetrieve({ uuid: document.uuid })
      if (response.url) {
        pdfUrl.value = response.url
        pdfFilename.value = document.title
        // Store document metadata for scanned PDF detection
        pdfDocument.value = {
          filesize: response.filesize,
          page_count: response.page_count
        }
        // Set initial page from document pages (similar to DatamapperSource)
        if (document.pages && document.pages.length > 0) {
          pdfInitialPage.value = document.pages[0]
        } else {
          pdfInitialPage.value = 1
        }
        isPdfOpen.value = true
      }
    } catch (error) {
      console.error('Failed to fetch document:', error)
    }
  }
  provide('handleDocumentClick', handleDocumentClick)

  const toggleDocumentPanel = () => {
    isDocumentPanelOpen.value = !isDocumentPanelOpen.value
  }
  provide('toggleDocumentPanel', toggleDocumentPanel)

  const route = useRoute()

  /** Fullscreen KI-Assistent: Figma layout without visible thread sidebar. */
  const isFullscreenAiAssistantLayout = computed(
    () =>
      props.sessionType === AIChatSessionTypeEnum.GLOBAL &&
      (route.path === ROUTES.APP.AI_ASSISTANT.ROOT ||
        route.path.startsWith(`${ROUTES.APP.AI_ASSISTANT.ROOT}/`))
  )

  const isDebugMode = computed(() => route.query.debug === 'true')
  provide('isDebugMode', isDebugMode)

  const debugPanelRef = ref()
  provide('debugPanelRef', debugPanelRef)

  const sidebarRef = ref()

  const { exportSessionPdf } = useChatPdfExport()

  const handleDownloadPdf = () => {
    const uuid = activeChatSession.value?.uuid
    if (uuid) exportSessionPdf(uuid)
  }

  const handleOpenPreferencesDialog = () => {
    if (sidebarRef.value) {
      sidebarRef.value.openPreferencesDialog()
    }
  }

  // Folder view handlers
  const handleRenameFolder = (folder: SessionGroup) => {
    editingFolder.value = folder
    showFolderDialog.value = true
  }

  const handleDeleteChat = (_session: AIChatSession) => {
    // The actual deletion is handled in the sidebar
    // This method can be used for any additional cleanup if needed
  }

  const handleCreateFolder = (sessionToMove?: AIChatSession) => {
    editingFolder.value = null
    showFolderDialog.value = true
    // Store the session to move after folder creation
    if (sessionToMove) {
      sessionToMoveToNewFolder.value = sessionToMove
    }
  }

  const handleFolderSave = async (folder: SessionGroup) => {
    try {
      // Close the dialog and reset state
      showFolderDialog.value = false
      editingFolder.value = null

      // If there's a session to move to this new folder, do it now
      if (sessionToMoveToNewFolder.value) {
        // Validate that the session has a valid UUID
        if (!sessionToMoveToNewFolder.value.uuid) {
          console.error('Session to move has no UUID:', sessionToMoveToNewFolder.value)
          sessionToMoveToNewFolder.value = null
          return
        }

        try {
          const updatedSession = await AiChatSessionsService.aiChatSessionsPartialUpdate({
            uuid: sessionToMoveToNewFolder.value.uuid,
            requestBody: {
              session_group_uuid: folder.uuid
            }
          })

          globalChatStore.updateChatSession(updatedSession)

          // Select the moved chat session
          changeActiveChatSession(updatedSession)
        } catch (error) {
          console.error('Failed to move chat to new folder:', error)
        }

        // Clear the session to move
        sessionToMoveToNewFolder.value = null
      }

      // Select the newly created folder in the sidebar
      if (sidebarRef.value) {
        // Use nextTick to ensure the folder is properly loaded in the store
        await nextTick()
        sidebarRef.value.selectFolder(folder.uuid)
      }

      // Select the newly created folder locally
      selectedFolder.value = folder

      // Emit folder update event
      emit('folderUpdated', folder)
    } catch (error) {
      console.error('Failed to handle folder save:', error)
    }
  }

  // Expose methods for parent component
  defineExpose({
    createFolder: handleCreateFolder
  })

  const handleNewChat = async (folder: SessionGroup) => {
    try {
      // Create a new chat session under the selected folder
      const newSession = await AiChatSessionsService.aiChatSessionsCreate({
        requestBody: {
          title: 'New Chat',
          session_type: AIChatSessionTypeEnum.GLOBAL,
          session_group_uuid: folder.uuid
        }
      })

      // Add the new session to the global chat store
      globalChatStore.chatSessions.unshift(newSession)

      // Set the new session as active in the store
      globalChatStore.setActiveChatSession(newSession)

      // Clear the selected folder since we now have an active chat session
      selectedFolder.value = null
      // Emit to sidebar to clear folder selection state
      emit('folderSelected', null)

      console.log('Created new chat session:', newSession.title)
    } catch (error) {
      console.error('Failed to create chat session:', error)
    }
  }

  const handleSendMessage = async (message: string, folder: SessionGroup) => {
    try {
      // Create a new chat session under the selected folder
      const newSession = await AiChatSessionsService.aiChatSessionsCreate({
        requestBody: {
          title: message.length > 50 ? message.substring(0, 50) + '...' : message,
          session_type: AIChatSessionTypeEnum.GLOBAL,
          session_group_uuid: folder.uuid
        }
      })

      // Add the new session to the global chat store
      globalChatStore.chatSessions.unshift(newSession)

      // Set the new session as active in the store
      globalChatStore.setActiveChatSession(newSession)

      // Clear the selected folder since we now have an active chat session
      selectedFolder.value = null
      // Emit to sidebar to clear folder selection state
      emit('folderSelected', null)

      // Use the existing sendChatMessage mechanism to get proper AI response polling
      await sendChatMessage(newSession, message)

      console.log('Created new chat session and sent message with AI response polling')
    } catch (error) {
      console.error('Failed to create chat session:', error)
    }
  }

  const handleFolderUpdated = (updatedFolder: SessionGroup) => {
    selectedFolder.value = updatedFolder
  }

  const handleSelectChatFromFolder = (session: AIChatSession) => {
    // Set the selected session as active
    globalChatStore.setActiveChatSession(session)
    // Clear the selected folder to show the chat
    selectedFolder.value = null
    // Also clear the folder selection in the sidebar by emitting the event
    // This will trigger the sidebar to clear its activeFolderId
    emit('folderSelected', null)
  }

  // Handle folder selection from sidebar
  const handleFolderSelected = (folder: SessionGroup | null) => {
    selectedFolder.value = folder
    // Clear active chat session when folder is selected
    if (folder) {
      globalChatStore.setActiveChatSession(null)
    }
    // If folder is null (cleared), we don't need to do anything else
    // The sidebar will handle clearing its own activeFolderId
  }

  // Handle moving chat to folder
  const handleMoveChatToFolder = (session: AIChatSession, folder: SessionGroup | null) => {
    // The actual move operation is handled in ChatFolderItem component
    // This is just for any additional cleanup or UI updates if needed
    console.log('Chat moved to folder:', session.title, folder?.name || 'No folder')
  }
</script>
<template>
  <div
    class="flex h-full w-full"
    :class="isFullscreenAiAssistantLayout ? 'bg-white' : ''"
    data-cy="global-ai-chat-container"
  >
    <div
      v-if="isInitialLoading"
      class="flex h-full w-full"
      :class="isFullscreenAiAssistantLayout ? '' : 'rounded-lg border border-gray-200'"
    >
      <!-- Sidebar Skeleton (hidden for fullscreen AI Assistant shell) -->
      <AiChatSidebarSkeleton v-if="!isFullscreenAiAssistantLayout" />

      <!-- Main content skeleton -->
      <AiChatMainSkeleton />
    </div>
    <div
      v-else-if="currentUser"
      class="flex h-full w-full min-w-0 flex-1"
      :class="isFullscreenAiAssistantLayout ? '' : 'rounded-lg border border-gray-200'"
    >
      <!-- Sidebar stays mounted (preferences dialog) but hidden on AI Assistant route -->
      <div :class="isFullscreenAiAssistantLayout ? 'hidden' : 'contents'">
        <AiChatSidebar
          ref="sidebarRef"
          :selected-folder="selectedFolder"
          @folder-selected="handleFolderSelected"
          @folder-updated="handleFolderUpdated"
          @create-folder="handleCreateFolder"
          @rename-folder="handleRenameFolder"
          @delete-chat="handleDeleteChat"
          @move-chat-to-folder="handleMoveChatToFolder"
          @switch-to-folders-view="() => {}"
        />
      </div>
      <FolderView
        v-if="shouldShowFolderView"
        :folder="selectedFolder!"
        @new-chat="handleNewChat"
        @send-message="handleSendMessage"
        @folder-updated="handleFolderUpdated"
        @select-chat="handleSelectChatFromFolder"
      />
      <AiChatMain
        v-else
        @open-preferences-dialog="handleOpenPreferencesDialog"
        @download-pdf="handleDownloadPdf"
      />
    </div>
    <div
      v-else
      class="rounded border border-red-300 bg-red-100 p-4"
    >
      <p class="text-red-700">No current user found.</p>
    </div>
    <AiChatDebugPanel
      v-if="currentUser"
      ref="debugPanelRef"
      :active-chat-session="activeChatSession"
      :generating-session-uuids="generatingSessionUuids"
      :current-user="currentUser"
    />
    <PDFViewer
      v-model:isOpen="isPdfOpen"
      v-model:url="pdfUrl"
      :filename="pdfFilename"
      :initial-page="pdfInitialPage"
      :scale="1.0"
      :document="pdfDocument"
    />
  </div>
  <!-- <EntityCard /> -->

  <!-- Folder Dialog -->
  <Teleport to="body">
    <ChatFolderDialog
      v-if="showFolderDialog"
      :open="showFolderDialog"
      :folder="editingFolder"
      @close="showFolderDialog = false"
      @saved="handleFolderSave"
    />
  </Teleport>
</template>

<style scoped>
  /* ... */
</style>
